<template>
  <div class="notifications-page">
    <!-- 標題區域 -->
    <div class="bg-white/80 backdrop-blur-sm rounded-2xl p-6 post-shadow mb-6">
      <div class="flex items-center justify-between">
        <h2 class="text-2xl font-bold text-wood-800 flex items-center">
          <i class="fas fa-bell mr-3 text-wood-600"></i>
          通知中心
        </h2>
        <div class="flex items-center space-x-2">
          <span class="text-sm text-wood-600">
            共 {{ notifications.length }} 個通知
          </span>
          <button 
            v-if="hasUnreadNotifications" 
            @click="markAllAsRead"
            class="bg-wood-500 hover:bg-wood-600 text-white px-4 py-2 rounded-lg transition-colors duration-200 text-sm"
          >
            <i class="fas fa-check mr-1"></i>
            標記全部已讀
          </button>
        </div>
      </div>
    </div>

    <!-- 載入中狀態 -->
    <div v-if="loading" class="flex justify-center items-center h-48">
      <div class="w-12 h-12 rounded-full yin-yang animate-pulse-soft"></div>
    </div>

    <!-- 通知列表 -->
    <div v-else-if="notifications.length > 0" class="space-y-4">
      <div 
        v-for="notification in notifications" 
        :key="notification.id"
        :class="[
          'bg-white/80 backdrop-blur-sm rounded-2xl p-6 post-shadow transition-all duration-300 hover:shadow-lg',
          { 'border-l-4 border-wood-500 bg-wood-50/80': !notification.read }
        ]"
      >
        <div class="flex items-start space-x-4">
          <!-- 頭像 -->
          <div class="flex-shrink-0">
            <img 
              v-if="notification.user2?.avatar" 
              :src="notification.user2.avatar" 
              :alt="notification.user2.nickname"
              class="w-12 h-12 rounded-full object-cover avatar-glow"
            />
            <div v-else class="w-12 h-12 rounded-full bg-wood-200 flex items-center justify-center">
              <i class="fas fa-user text-wood-500"></i>
            </div>
          </div>

          <!-- 通知內容 -->
          <div class="flex-1 min-w-0">
            <div class="flex items-start justify-between">
              <div class="flex-1 min-w-0">
                <p class="text-wood-800 leading-relaxed">
                  <template v-if="notification.content === 'follow'">
                    <template v-if="notification.user2">
                      <button 
                        @click="navigateToUser(notification.user2.uid)"
                        class="font-medium text-wood-700 hover:text-wood-900 hover:underline transition-colors"
                      >
                        {{ notification.user2.nickname }}
                      </button>
                      開始追蹤你。
                    </template>
                    <template v-else>
                      <span class="text-wood-500">使用者已不存在</span>
                    </template>
                  </template>

                  <template v-else-if="notification.content === 'like'">
                    <template v-if="notification.user2">
                      <button 
                        @click="navigateToUser(notification.user2.uid)"
                        class="font-medium text-wood-700 hover:text-wood-900 hover:underline transition-colors"
                      >
                        {{ notification.user2.nickname }}
                      </button>
                      喜歡你的
                      <button 
                        v-if="notification.post"
                        @click="navigateToPost(notification.post.id)"
                        class="text-wood-600 hover:text-wood-800 hover:underline transition-colors"
                      >
                        貼文
                      </button>
                      <template v-if="notification.post">
                        （<span class="text-wood-600">{{ truncateText(notification.post.short, 30) }}</span>）
                      </template>
                      。
                    </template>
                    <template v-else>
                      <span class="text-wood-500">使用者已不存在</span>
                    </template>
                  </template>

                  <template v-else-if="notification.content === 'reply'">
                    <template v-if="notification.user2">
                      <button 
                        @click="navigateToUser(notification.user2.uid)"
                        class="font-medium text-wood-700 hover:text-wood-900 hover:underline transition-colors"
                      >
                        {{ notification.user2.nickname }}
                      </button>
                      回覆你的
                      <button 
                        v-if="notification.post"
                        @click="navigateToPost(notification.post.id)"
                        class="text-wood-600 hover:text-wood-800 hover:underline transition-colors"
                      >
                        貼文
                      </button>
                      <template v-if="notification.post">
                        （<span class="text-wood-600">{{ truncateText(notification.post.short, 30) }}</span>）
                      </template>
                      。
                    </template>
                    <template v-else>
                      <span class="text-wood-500">使用者已不存在</span>
                    </template>
                  </template>

                  <template v-else-if="notification.content === 'club_join'">
                    <template v-if="notification.user2">
                      <button 
                        @click="navigateToUser(notification.user2.uid)"
                        class="font-medium text-wood-700 hover:text-wood-900 hover:underline transition-colors"
                      >
                        {{ notification.user2.nickname }}
                      </button>
                      申請加入你管理的武場
                      <button 
                        v-if="notification.club"
                        @click="navigateToClub(notification.club.id)"
                        class="text-wood-600 hover:text-wood-800 hover:underline transition-colors"
                      >
                        {{ notification.club.name }}
                      </button>
                    </template>
                    <template v-else>
                      <span class="text-wood-500">使用者已不存在</span>
                    </template>
                  </template>

                  <template v-else-if="notification.content === 'club_agree'">
                    <button 
                      v-if="notification.club"
                      @click="navigateToClub(notification.club.id)"
                      class="text-wood-600 hover:text-wood-800 hover:underline transition-colors"
                    >
                      {{ notification.club.name }}
                    </button>
                    武場管理員已經同意你的加入。
                  </template>

                  <template v-else-if="notification.content === 'club_member_post'">
                    你所在的武場
                    <button 
                      v-if="notification.club"
                      @click="navigateToClub(notification.club.id)"
                      class="text-wood-600 hover:text-wood-800 hover:underline transition-colors"
                    >
                      {{ notification.club.name }}
                    </button>
                    有新的貼文
                    <template v-if="notification.post">
                      （<button 
                        @click="navigateToPost(notification.post.id)"
                        class="text-wood-600 hover:text-wood-800 hover:underline transition-colors"
                      >
                        {{ truncateText(notification.post.short, 30) }}
                      </button>）
                    </template>
                    。
                  </template>

                  <template v-else>
                    {{ notification.content }}
                  </template>
                </p>
              </div>
              
              <!-- 未讀標記 -->
              <div v-if="!notification.read" class="flex-shrink-0 ml-4">
                <div class="w-3 h-3 bg-wood-500 rounded-full animate-pulse"></div>
              </div>
            </div>
            
            <!-- 時間戳 -->
            <div class="mt-2 text-sm text-wood-500">
              {{ formatDate(notification.created_at) }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 空狀態 -->
    <div v-else class="text-center py-12">
      <div class="bg-white/80 backdrop-blur-sm rounded-2xl p-12 post-shadow">
        <div class="w-16 h-16 mx-auto mb-4 rounded-full bg-wood-100 flex items-center justify-center">
          <i class="fas fa-bell-slash text-wood-400 text-2xl"></i>
        </div>
        <h3 class="text-lg font-medium text-wood-800 mb-2">暫無通知</h3>
        <p class="text-wood-600">當有新的互動時，通知會顯示在這裡</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { usePostsStore } from '../stores/posts.js'
import { useUserStore } from '../stores/user.js'
import axios from 'axios'

const postsStore = usePostsStore()
const userStore = useUserStore()

// 資料狀態
const notifications = ref([])
const loading = ref(true)

// 計算屬性
const hasUnreadNotifications = computed(() => {
  return notifications.value.some(n => !n.read)
})

// 獲取通知列表
const loadNotifications = async () => {
  try {
    loading.value = true
    const response = await axios.get('/home/notifications.json')
    notifications.value = response.data || []

    // 載入通知後，自動標記所有通知為已讀
    if (notifications.value.some(n => !n.read)) {
      await markAllAsRead()
    }

    // 重置未讀數量（因為用戶已經查看了通知頁面）
    userStore.clearUnreadNotifications()
  } catch (error) {
    console.error('載入通知失敗:', error)
  } finally {
    loading.value = false
  }
}

// 標記全部已讀
const markAllAsRead = async () => {
  try {
    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content')
    
    await axios.post('/home/mark_notifications_read', {}, {
      headers: {
        'X-CSRF-Token': csrfToken,
        'Accept': 'application/json'
      }
    })
    
    // 更新本地狀態
    notifications.value.forEach(notification => {
      notification.read = true
    })
    
    // 更新 userStore 中的未讀數量
    userStore.clearUnreadNotifications()
    
    console.log('通知已標記為已讀')
  } catch (error) {
    console.error('標記已讀失敗:', error)
  }
}

// 導航方法
const navigateToUser = (uid) => {
  const url = `/users/${uid}`
  window.history.pushState(null, '', url)
  postsStore.updateCurrentPath(url)
}

const navigateToPost = (postId) => {
  const url = `/posts/${postId}`
  window.history.pushState(null, '', url)
  postsStore.updateCurrentPath(url)
  postsStore.clearPosts()
  postsStore.ready = true
}

const navigateToClub = (clubId) => {
  const url = `/clubs/${clubId}`
  window.history.pushState(null, '', url)
  postsStore.updateCurrentPath(url)
  postsStore.clearPostDetail()
  postsStore.clearPosts()
  postsStore.ready = true
}

// 輔助方法
const truncateText = (text, maxLength) => {
  if (!text) return ''
  // 移除 HTML 標籤
  const cleanText = text.replace(/<[^>]*>/g, '')
  if (cleanText.length <= maxLength) return cleanText
  return cleanText.substring(0, maxLength) + '...'
}

const formatDate = (dateString) => {
  const date = new Date(dateString)
  const now = new Date()
  const diff = now - date
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)
  
  if (minutes < 1) return '剛剛'
  if (minutes < 60) return `${minutes} 分鐘前`
  if (hours < 24) return `${hours} 小時前`
  if (days < 30) return `${days} 天前`
  
  return date.toLocaleDateString('zh-TW', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

// 組件掛載時載入通知
onMounted(() => {
  loadNotifications()
})
</script>

<style scoped>
.notifications-page {
  max-width: 4xl;
  margin: 0 auto;
}

.avatar-glow {
  box-shadow: 0 0 0 3px rgba(166, 139, 91, 0.3);
}

.post-shadow {
  box-shadow: 
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06),
    0 0 0 1px rgba(196, 166, 116, 0.1);
}

.animate-pulse-soft {
  animation: pulseSoft 2s ease-in-out infinite;
}

@keyframes pulseSoft {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

.yin-yang {
  background: conic-gradient(from 0deg, #000 0deg 180deg, #fff 180deg 360deg);
  position: relative;
}

.yin-yang::before {
  content: '';
  position: absolute;
  top: 25%;
  left: 50%;
  width: 50%;
  height: 50%;
  background: #fff;
  border-radius: 50%;
  transform: translateX(-50%);
}

.yin-yang::after {
  content: '';
  position: absolute;
  top: 75%;
  left: 50%;
  width: 50%;
  height: 50%;
  background: #000;
  border-radius: 50%;
  transform: translateX(-50%);
}
</style>